import { SUPPORTED_PLACE_HOLDERS } from '@modeso/aumico-lib-core-fe/constants/constants';

/**
 * Paints the review marking onto the print preview — on-screen DOM only; the export builds from the
 * translated clone, which this never touches. Matched whole-node, not by substring: "Übrige" would
 * otherwise hit inside "Übrige Forderungen". The only text allowed around a match is a decoration the
 * print mappers add themselves (number, "Total", "in CHF"), so the rest of the node is never guessed.
 */
const MARK_CLASS = 'translation-mark';
const BLOCK_MARK_CLASS = `${MARK_CLASS}--block`;
const SOURCE_ATTRIBUTE = 'data-translation-source';
// A rich-text mark is the whole paragraph block; its edit field must start from the stored HTML, not
// from the flattened text, or a correction would drop the formatting.
const SHOWN_ATTRIBUTE = 'data-translation-shown';
// Section titles have their own edit button, and a same-named mapping entry (e.g. "Bilan") would
// otherwise mark them and edit that entry instead of the title. The table of contents only repeats
// those titles and follows them.
const SKIPPED_ANCESTOR_SELECTOR = '.sectionTitle, aumico-annual-report-toc-page';
// Rich text (notes free text, complementary reports, AOP/EMS texts) is injected HTML; whatever holds
// these block elements directly is where a stored HTML value was put in.
const RICH_TEXT_BLOCKS = 'p, ul, ol, h1, h2, h3, h4, h5, h6, table, blockquote, figure';
const CURRENCY_CODE = '[A-Z]{3}';
// The print replaces each placeholder ("@ar.company") with its value, so a stored text that carries one
// never equals what the preview shows; there the placeholder stands for any text. Longest first:
// "@ar.duedate" must not eat the start of "@ar.duedate-of-previous-year".
const PLACEHOLDER = new RegExp(
    `(${[...SUPPORTED_PLACE_HOLDERS]
        .sort((a, b) => b.length - a.length)
        .map((placeholder) => placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
        .join('|')})`,
);

export interface ITranslationMarkValue {
    /** The report-language text — the key of the block entry, needed to edit it. */
    source: string;
    kind: 'ai' | 'user' | 'plain';
}

/** The words the print mappers put around a translated text, in the language shown. */
export interface ITranslationDecorations {
    /** Account numbers are printed (`1000 Kasse`, reclassification accounts `MAN …`). */
    accountNumbersShown?: boolean;
    /** The localized "Total" in front of a group total. */
    totalWord?: string;
    /** The localized "in" before the currency ("Aktiven in CHF"). */
    wordBeforeCurrency?: string;
}

export interface IDecoratedMatch {
    prefix: string;
    text: string;
    suffix: string;
}

export class TranslationMarkingHelper {
    static mark(
        root: HTMLElement | null,
        values: Map<string, ITranslationMarkValue>,
        decorations: ITranslationDecorations = {},
    ): void {
        if (root == null || values.size === 0) {
            return;
        }
        TranslationMarkingHelper.clear(root);
        TranslationMarkingHelper.markRichTexts(root, values);

        const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
        const hits: { textNode: Text; match: IDecoratedMatch }[] = [];
        let node = walker.nextNode() as Text | null;
        while (node != null) {
            const parent = node.parentElement;
            if (parent != null && parent.closest(`${SKIPPED_ANCESTOR_SELECTOR}, .${MARK_CLASS}`) == null) {
                const match = TranslationMarkingHelper.matchDecorated(node.textContent ?? '', values, decorations);
                if (match != null) {
                    hits.push({ textNode: node, match });
                }
            }
            node = walker.nextNode() as Text | null;
        }

        hits.forEach(({ textNode, match }) => {
            const value = values.get(match.text.trim());
            const parent = textNode.parentElement;
            if (value == null || parent == null) {
                return;
            }
            const span = document.createElement('span');
            span.className = `${MARK_CLASS} ${MARK_CLASS}--${value.kind}`;
            span.setAttribute(SOURCE_ATTRIBUTE, value.source);
            span.textContent = match.text;
            // The decoration stays plain text around the mark, so only the translated part is marked.
            if (match.prefix !== '') {
                parent.insertBefore(document.createTextNode(match.prefix), textNode);
            }
            parent.replaceChild(span, textNode);
            if (match.suffix !== '') {
                parent.insertBefore(document.createTextNode(match.suffix), span.nextSibling);
            }
        });
    }

    /**
     * The whole node, or the node minus the decorations the print mappers add: a leading account
     * number / notes numbering ("1.1.2") and "Total", a trailing "in CHF" / "CHF". Whatever is left
     * must be a known text as a whole — anything else in the node means no match.
     */
    static matchDecorated(
        content: string,
        values: Map<string, unknown>,
        decorations: ITranslationDecorations = {},
    ): IDecoratedMatch | null {
        if (values.has(content.trim())) {
            return { prefix: '', text: content, suffix: '' };
        }

        const number = decorations.accountNumbersShown ? '(?:\\d+(?:\\.\\d+)*\\.?|MAN)' : '\\d+(?:\\.\\d+)*\\.?';
        const total = decorations.totalWord ? `(?:${TranslationMarkingHelper.escape(decorations.totalWord)}\\s+)?` : '';
        const prefix = new RegExp(`^(\\s*(?:${number}\\s+)?${total})`);
        const inWord = decorations.wordBeforeCurrency
            ? `(?:${TranslationMarkingHelper.escape(decorations.wordBeforeCurrency)}\\s+)?`
            : '';
        const suffix = new RegExp(`(\\s+${inWord}${CURRENCY_CODE}\\s*)$`);

        const prefixText = content.match(prefix)?.[1] ?? '';
        const suffixText = content.slice(prefixText.length).match(suffix)?.[1] ?? '';
        const cut = (head: string, tail: string): IDecoratedMatch => ({
            prefix: head,
            text: content.slice(head.length, content.length - tail.length),
            suffix: tail,
        });

        // Widest cut first, then each side alone: a name that itself ends in a currency code
        // ("Darlehen EUR") must still match when only the prefix is decoration.
        const candidates = [cut(prefixText, suffixText), cut(prefixText, ''), cut('', suffixText)];

        return (
            candidates.find(
                (candidate) =>
                    (candidate.prefix !== '' || candidate.suffix !== '') &&
                    candidate.text.trim() !== '' &&
                    values.has(candidate.text.trim()),
            ) ?? null
        );
    }

    /**
     * The text a reader sees, without any whitespace: the browser joins paragraphs without a space
     * ("<p>Eins</p><p>Zwei</p>" reads "EinsZwei"), and the print adds line breaks to empty paragraphs,
     * so spacing is not comparable between the stored HTML and the rendered block.
     */
    static comparable(text: string): string {
        return text.replace(/[\s\u00a0]+/g, '');
    }

    /** Puts every marked node back, so the preview renders as it did before. */
    static clear(root: HTMLElement | null): void {
        root?.querySelectorAll(`.${BLOCK_MARK_CLASS}`).forEach((element) => {
            element.classList.remove(MARK_CLASS, BLOCK_MARK_CLASS, `${MARK_CLASS}--ai`, `${MARK_CLASS}--user`, `${MARK_CLASS}--plain`);
            element.removeAttribute(SOURCE_ATTRIBUTE);
            element.removeAttribute(SHOWN_ATTRIBUTE);
        });
        root?.querySelectorAll(`.${MARK_CLASS}`).forEach((span) => {
            span.replaceWith(document.createTextNode(span.textContent ?? ''));
        });
        // Glues a split row back into one text node, so the next paint sees "1000 Kasse" again.
        root?.normalize();
    }

    /** The clicked mark: its source (the block key), the text it shows right now, and the mark itself. */
    static markOfClick(target: EventTarget | null): { source: string; shown: string; element: HTMLElement } | null {
        const element = target instanceof HTMLElement ? target.closest<HTMLElement>(`.${MARK_CLASS}`) : null;
        const source = element?.getAttribute(SOURCE_ATTRIBUTE);
        if (element == null || source == null) {
            return null;
        }

        return { source, shown: element.getAttribute(SHOWN_ATTRIBUTE) ?? element.textContent?.trim() ?? '', element };
    }

    /** Every mark of one source, in page order — a correction repaints all of them. */
    static marksOf(root: HTMLElement | null, source: string): HTMLElement[] {
        return Array.from(root?.querySelectorAll<HTMLElement>(`.${MARK_CLASS}`) ?? []).filter(
            (element) => element.getAttribute(SOURCE_ATTRIBUTE) === source,
        );
    }

    /** The box that scrolls the preview: the nearest ancestor that actually overflows, else the page. */
    static scrollParent(element: HTMLElement): HTMLElement {
        for (let parent = element.parentElement; parent != null; parent = parent.parentElement) {
            const overflowY = getComputedStyle(parent).overflowY;
            if ((overflowY === 'auto' || overflowY === 'scroll') && parent.scrollHeight > parent.clientHeight) {
                return parent;
            }
        }

        return (document.scrollingElement as HTMLElement | null) ?? document.documentElement;
    }

    /**
     * A rich text is HTML: the print splits it into one text node per paragraph or formatting run, so
     * no single node ever equals the stored value. Compared as a whole block instead, by its text.
     */
    private static markRichTexts(root: HTMLElement, values: Map<string, ITranslationMarkValue>): void {
        const byText = new Map<string, { html: string; value: ITranslationMarkValue }>();
        const withPlaceholders: { pattern: RegExp; html: string; value: ITranslationMarkValue }[] = [];
        const parser = new DOMParser();
        values.forEach((value, html) => {
            if (/<[a-z][^>]*>/i.test(html)) {
                // The browser decodes entities the same way it did for the rendered block.
                const text = TranslationMarkingHelper.comparable(parser.parseFromString(html, 'text/html').body.textContent ?? '');
                const pattern = TranslationMarkingHelper.placeholderPattern(text);
                if (pattern != null) {
                    withPlaceholders.push({ pattern, html, value });
                } else {
                    byText.set(text, { html, value });
                }
            }
        });
        if (byText.size === 0 && withPlaceholders.length === 0) {
            return;
        }

        const containers = new Set<HTMLElement>();
        root.querySelectorAll<HTMLElement>(RICH_TEXT_BLOCKS).forEach((block) => {
            if (block.parentElement != null && root.contains(block.parentElement)) {
                containers.add(block.parentElement);
            }
        });

        // Innermost first, and an area that already holds a mark is left alone: a placeholder matches
        // any text, so "@org.name hello" would otherwise also fit the wrapper around title + content.
        const depth = (element: HTMLElement): number => {
            let count = 0;
            for (let parent = element.parentElement; parent != null; parent = parent.parentElement) {
                count++;
            }

            return count;
        };
        [...containers].sort((a, b) => depth(b) - depth(a)).forEach((element) => {
            if (
                element.closest(`${SKIPPED_ANCESTOR_SELECTOR}, .${MARK_CLASS}`) != null ||
                element.querySelector(`.${MARK_CLASS}`) != null
            ) {
                return;
            }
            const shown = TranslationMarkingHelper.comparable(element.textContent ?? '');
            const hit = byText.get(shown) ?? withPlaceholders.find(({ pattern }) => pattern.test(shown));
            if (hit == null) {
                return;
            }
            element.classList.add(MARK_CLASS, BLOCK_MARK_CLASS, `${MARK_CLASS}--${hit.value.kind}`);
            element.setAttribute(SOURCE_ATTRIBUTE, hit.value.source);
            element.setAttribute(SHOWN_ATTRIBUTE, hit.html);
        });
    }

    /**
     * The stored text as a pattern in which each placeholder matches whatever the print put there; the
     * rest must still match exactly. Null when the text has no placeholder.
     */
    static placeholderPattern(text: string): RegExp | null {
        const parts = text.split(PLACEHOLDER);
        if (parts.length === 1) {
            return null;
        }
        // split() with a capture group interleaves literal text (even indexes) and placeholders (odd).
        const source = parts
            .map((part, index) => (index % 2 === 1 ? '.*?' : TranslationMarkingHelper.escape(part)))
            .join('');

        return new RegExp(`^${source}$`);
    }

    private static escape(word: string): string {
        return word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }
}
